<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useBranding } from 'shared/composables/useBranding';
import CetakersFlowLogsAPI from 'dashboard/api/integrations/cetakersFlowLogs';
import CetakersLogDetailDialog from './components/CetakersLogDetailDialog.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { replaceInstallationName } = useBranding();

const description = computed(() =>
  replaceInstallationName(
    'Cetakers Flow adalah sistem manajemen operasional percetakan. Dengan integrasi ini, Anda dapat menghubungkan data dari Cetakers Pro langsung ke dalam Chatwoot, seperti melihat data Invoice dan SPK pelanggan langsung di panel obrolan.'
  )
);

const apiKey = ref('');
const apiEndpoint = ref('');
const captainEnabled = ref(true);
const captainScopes = ref({
  live_chat: true,
  copilot: true,
  playground: true,
});
const captainModules = ref({
  spk: true,
  products: true,
  invoices: true,
  shipping: true,
  customers: true,
  channels: true,
  create_order: true,
});
const isSaving = ref(false);
const showConfigModal = ref(false);

const logs = ref([]);
const metrics = ref({
  total_requests: 0,
  success_count: 0,
  failed_count: 0,
  avg_latency_ms: 0,
});
const isFetchingLogs = ref(false);
const selectedLog = ref(null);
const showDetailDialog = ref(false);

const connectedHook = computed(() => {
  const integration = store.getters['integrations/getIntegration']('cetakers_flow');
  return integration?.hooks?.[0] || null;
});

const isConnected = computed(() => !!connectedHook.value);

const maskedKey = computed(() => {
  if (!connectedHook.value?.settings?.api_key) return '';
  const key = connectedHook.value.settings.api_key;
  return key.length > 8 ? `${key.slice(0, 4)}${'*'.repeat(key.length - 8)}${key.slice(-4)}` : '****';
});

const fetchLogs = async () => {
  if (!isConnected.value) return;
  isFetchingLogs.value = true;
  try {
    const { data } = await CetakersFlowLogsAPI.getLogs();
    logs.value = data.logs || [];
    metrics.value = data.metrics || {
      total_requests: 0,
      success_count: 0,
      failed_count: 0,
      avg_latency_ms: 0,
    };
  } catch (e) {
    // silent
  } finally {
    isFetchingLogs.value = false;
  }
};

const openLogDetail = (log) => {
  selectedLog.value = log;
  showDetailDialog.value = true;
};

onMounted(async () => {
  await store.dispatch('integrations/get');
  fetchLogs();
});

const activeScopeTab = ref('live_chat');

const defaultModulesForScope = (scope) => {
  if (scope === 'playground') {
    return {
      spk: false,
      products: true,
      invoices: false,
      shipping: false,
      customers: false,
      channels: false,
      create_order: false,
    };
  }
  return {
    spk: true,
    products: true,
    invoices: true,
    shipping: true,
    customers: true,
    channels: true,
    create_order: scope === 'copilot',
  };
};

const captainScopeModules = ref({
  live_chat: defaultModulesForScope('live_chat'),
  copilot: defaultModulesForScope('copilot'),
  playground: defaultModulesForScope('playground'),
});

const isScopeEnabled = (scope) => captainScopes.value[scope] !== false;

const toggleScopeEnabled = (scope, val) => {
  captainScopes.value[scope] = val;
};

const selectAllModulesForScope = (scope) => {
  Object.keys(captainScopeModules.value[scope]).forEach((mod) => {
    captainScopeModules.value[scope][mod] = true;
  });
};

const clearAllModulesForScope = (scope) => {
  Object.keys(captainScopeModules.value[scope]).forEach((mod) => {
    captainScopeModules.value[scope][mod] = false;
  });
};

const selectProductsOnlyForScope = (scope) => {
  Object.keys(captainScopeModules.value[scope]).forEach((mod) => {
    captainScopeModules.value[scope][mod] = mod === 'products';
  });
};

const openConfigModal = () => {
  if (connectedHook.value) {
    const settings = connectedHook.value.settings || {};
    apiKey.value = settings.api_key || '';
    apiEndpoint.value = settings.api_endpoint || '';
    captainEnabled.value = settings.captain_enabled !== false;
    if (settings.captain_scopes) {
      captainScopes.value = { ...captainScopes.value, ...settings.captain_scopes };
    }
    if (settings.captain_modules) {
      captainModules.value = { ...captainModules.value, ...settings.captain_modules };
    }
    if (settings.captain_scope_modules) {
      captainScopeModules.value = {
        live_chat: { ...defaultModulesForScope('live_chat'), ...settings.captain_scope_modules.live_chat },
        copilot: { ...defaultModulesForScope('copilot'), ...settings.captain_scope_modules.copilot },
        playground: { ...defaultModulesForScope('playground'), ...settings.captain_scope_modules.playground },
      };
    } else {
      // Migrate from legacy captain_modules & captain_scopes if available
      const legacyModules = settings.captain_modules || {};
      ['live_chat', 'copilot', 'playground'].forEach((scope) => {
        const scopeActive = settings.captain_scopes ? settings.captain_scopes[scope] !== false : true;
        captainScopeModules.value[scope] = {
          spk: scopeActive && (legacyModules.spk !== false),
          products: scopeActive && (legacyModules.products !== false),
          invoices: scopeActive && (legacyModules.invoices !== false),
          shipping: scopeActive && (legacyModules.shipping !== false),
          customers: scopeActive && (legacyModules.customers !== false),
          channels: scopeActive && (legacyModules.channels !== false),
          create_order: scopeActive && (legacyModules.create_order === true),
        };
      });
    }
  } else {
    apiKey.value = '';
    apiEndpoint.value = '';
    captainEnabled.value = true;
    captainScopes.value = {
      live_chat: true,
      copilot: true,
      playground: true,
    };
    captainModules.value = {
      spk: true,
      products: true,
      invoices: true,
      shipping: true,
      customers: true,
      channels: true,
      create_order: true,
    };
    captainScopeModules.value = {
      live_chat: defaultModulesForScope('live_chat'),
      copilot: defaultModulesForScope('copilot'),
      playground: defaultModulesForScope('playground'),
    };
  }
  showConfigModal.value = true;
};

const closeConfigModal = () => {
  showConfigModal.value = false;
  apiKey.value = '';
  apiEndpoint.value = '';
};

const handleConnect = async () => {
  if (!apiKey.value.trim()) return;
  isSaving.value = true;

  // Sync global captain_modules summary for backward compatibility
  const globalModules = { spk: false, products: false, invoices: false, shipping: false, customers: false, channels: false, create_order: false };
  ['live_chat', 'copilot', 'playground'].forEach((scope) => {
    if (captainScopes.value[scope]) {
      Object.keys(globalModules).forEach((mod) => {
        if (captainScopeModules.value[scope]?.[mod]) {
          globalModules[mod] = true;
        }
      });
    }
  });
  captainModules.value = globalModules;

  try {
    if (connectedHook.value) {
      await store.dispatch('integrations/deleteHook', {
        appId: 'cetakers_flow',
        hookId: connectedHook.value.id,
      });
    }
    await store.dispatch('integrations/createHook', {
      app_id: 'cetakers_flow',
      settings: {
        api_key: apiKey.value.trim(),
        api_endpoint: apiEndpoint.value.trim() || undefined,
        captain_enabled: captainEnabled.value,
        captain_scopes: captainScopes.value,
        captain_modules: captainModules.value,
        captain_scope_modules: captainScopeModules.value,
      },
    });
    useAlert('Konfigurasi Cetakers Flow & Scope Matrix Captain AI berhasil disimpan.');
    closeConfigModal();
  } catch {
    useAlert('Gagal menyimpan konfigurasi Cetakers Flow. Periksa API Key Anda dan coba lagi.');
  } finally {
    isSaving.value = false;
  }
};

const handleDisconnect = async () => {
  if (!connectedHook.value) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm('Apakah Anda yakin ingin memutus sambungan Cetakers Flow? Fitur dokumen dan menu Cetakers Flow akan dinonaktifkan.')) return;
  isSaving.value = true;
  try {
    await store.dispatch('integrations/deleteHook', {
      appId: 'cetakers_flow',
      hookId: connectedHook.value.id,
    });
    useAlert('Cetakers Flow berhasil diputus.');
  } catch {
    useAlert('Gagal memutus sambungan Cetakers Flow.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        title="Cetakers Flow"
        description=""
        back-button-label="Integrations"
      />
    </template>

    <template #body>
      <div class="w-full">
        <div class="outline outline-n-container outline-1 bg-n-card rounded-xl flex-grow overflow-auto p-4">
          <div class="flex items-center justify-center">
            <div class="flex h-16 w-16 items-center justify-center shrink-0">
              <img
                :src="'/dashboard/images/integrations/cetakers_flow.png'"
                alt="Cetakers Flow"
                class="max-w-full rounded-md border border-n-weak shadow-sm block bg-n-alpha-3 dark:bg-n-alpha-2"
              />
            </div>
            <div class="flex flex-col justify-center m-0 mx-4 flex-1">
              <h3 class="mb-1 text-heading-1 text-n-slate-12">
                Cetakers Flow
              </h3>
              <p class="text-n-slate-11 text-body-main">
                {{ description }}
              </p>
            </div>
            <div class="flex justify-center items-center mb-0 gap-2 shrink-0">
              <template v-if="isConnected">
                <Button
                  color="blue"
                  variant="faded"
                  @click="openConfigModal"
                >
                  Edit Konfigurasi
                </Button>
                <Button
                  color="ruby"
                  variant="faded"
                  :is-loading="isSaving"
                  @click="handleDisconnect"
                >
                  Putuskan
                </Button>
              </template>
              <template v-else>
                <Button
                  color="blue"
                  variant="faded"
                  @click="openConfigModal"
                >
                  Sambungkan
                </Button>
              </template>
            </div>
          </div>

          <!-- Konfigurasi aktif -->
          <div v-if="isConnected" class="mt-6 border-t border-n-weak pt-4 flex flex-col gap-4">
            <h4 class="text-sm font-semibold text-n-slate-12">Konfigurasi Aktif</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="flex flex-col gap-1 bg-n-alpha-1 p-3 rounded-lg border border-n-weak">
                <span class="text-xs font-medium text-n-slate-11">API KEY</span>
                <span class="font-mono text-sm text-n-slate-12">{{ maskedKey }}</span>
              </div>
              <div class="flex flex-col gap-1 bg-n-alpha-1 p-3 rounded-lg border border-n-weak">
                <span class="text-xs font-medium text-n-slate-11">API ENDPOINT</span>
                <span class="font-mono text-sm text-n-slate-12">{{ connectedHook?.settings?.api_endpoint || 'https://app.cetakers.com/fp-cetakers/api/v1' }}</span>
              </div>
            </div>

            <!-- Ringkasan Akses Captain AI -->
            <div class="mt-2 bg-n-card border border-n-weak rounded-xl p-4 flex flex-col gap-3 shadow-xs">
              <div class="flex items-center justify-between">
                <span class="text-sm font-bold text-n-slate-12 flex items-center gap-2">
                  <span class="i-lucide-bot size-4 text-w-brand-primary"></span> Status Integrasi Captain AI
                </span>
                <span class="px-2.5 py-0.5 text-xs font-bold rounded-full" :class="connectedHook?.settings?.captain_enabled !== false ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-n-alpha-2 text-n-slate-11'">
                  {{ connectedHook?.settings?.captain_enabled !== false ? 'AKTIF (HYBRID TOOLS)' : 'NONAKTIF' }}
                </span>
              </div>

              <!-- Scope Target Status Badges -->
              <div class="flex flex-wrap items-center gap-2 pt-1 border-b border-n-weak pb-3">
                <span class="text-xs font-medium text-n-slate-11">Scope Penggunaan:</span>
                <span class="px-2 py-0.5 text-[11px] font-semibold rounded-md border flex items-center gap-1" :class="connectedHook?.settings?.captain_scopes?.live_chat !== false ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800' : 'bg-slate-50 text-slate-400 border-slate-200'">
                  <span class="i-lucide-message-square size-3"></span> Live Percakapan: {{ connectedHook?.settings?.captain_scopes?.live_chat !== false ? 'ON' : 'OFF' }}
                </span>
                <span class="px-2 py-0.5 text-[11px] font-semibold rounded-md border flex items-center gap-1" :class="connectedHook?.settings?.captain_scopes?.copilot !== false ? 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800' : 'bg-slate-50 text-slate-400 border-slate-200'">
                  <span class="i-lucide-bot size-3"></span> Agent Copilot: {{ connectedHook?.settings?.captain_scopes?.copilot !== false ? 'ON' : 'OFF' }}
                </span>
                <span class="px-2 py-0.5 text-[11px] font-semibold rounded-md border flex items-center gap-1" :class="connectedHook?.settings?.captain_scopes?.playground !== false ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800' : 'bg-slate-50 text-slate-400 border-slate-200'">
                  <span class="i-lucide-flask-conical size-3"></span> Playground Testing: {{ connectedHook?.settings?.captain_scopes?.playground !== false ? 'ON' : 'OFF' }}
                </span>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 pt-1">
                <div class="p-2.5 rounded-lg border border-n-weak bg-n-alpha-1 flex items-center justify-between text-xs">
                  <span class="font-semibold text-n-slate-12 flex items-center gap-2">
                    <span class="i-lucide-file-text size-4 text-blue-600"></span> Data SPK & Order
                  </span>
                  <span class="text-[10px] font-bold px-2 py-0.5 rounded-full" :class="connectedHook?.settings?.captain_modules?.spk !== false ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-slate-100 text-slate-500'">
                    {{ connectedHook?.settings?.captain_modules?.spk !== false ? 'AKTIF' : 'NONAKTIF' }}
                  </span>
                </div>

                <div class="p-2.5 rounded-lg border border-n-weak bg-n-alpha-1 flex items-center justify-between text-xs">
                  <span class="font-semibold text-n-slate-12 flex items-center gap-2">
                    <span class="i-lucide-shopping-bag size-4 text-indigo-600"></span> Master Produk & Harga
                  </span>
                  <span class="text-[10px] font-bold px-2 py-0.5 rounded-full" :class="connectedHook?.settings?.captain_modules?.products !== false ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-slate-100 text-slate-500'">
                    {{ connectedHook?.settings?.captain_modules?.products !== false ? 'AKTIF' : 'NONAKTIF' }}
                  </span>
                </div>

                <div class="p-2.5 rounded-lg border border-n-weak bg-n-alpha-1 flex items-center justify-between text-xs">
                  <span class="font-semibold text-n-slate-12 flex items-center gap-2">
                    <span class="i-lucide-credit-card size-4 text-emerald-600"></span> Data Invoice & Tagihan
                  </span>
                  <span class="text-[10px] font-bold px-2 py-0.5 rounded-full" :class="connectedHook?.settings?.captain_modules?.invoices !== false ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-slate-100 text-slate-500'">
                    {{ connectedHook?.settings?.captain_modules?.invoices !== false ? 'AKTIF' : 'NONAKTIF' }}
                  </span>
                </div>

                <div class="p-2.5 rounded-lg border border-n-weak bg-n-alpha-1 flex items-center justify-between text-xs">
                  <span class="font-semibold text-n-slate-12 flex items-center gap-2">
                    <span class="i-lucide-truck size-4 text-amber-600"></span> Pengiriman & Resi
                  </span>
                  <span class="text-[10px] font-bold px-2 py-0.5 rounded-full" :class="connectedHook?.settings?.captain_modules?.shipping !== false ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-slate-100 text-slate-500'">
                    {{ connectedHook?.settings?.captain_modules?.shipping !== false ? 'AKTIF' : 'NONAKTIF' }}
                  </span>
                </div>

                <div class="p-2.5 rounded-lg border border-n-weak bg-n-alpha-1 flex items-center justify-between text-xs">
                  <span class="font-semibold text-n-slate-12 flex items-center gap-2">
                    <span class="i-lucide-users size-4 text-purple-600"></span> Data Master Pelanggan
                  </span>
                  <span class="text-[10px] font-bold px-2 py-0.5 rounded-full" :class="connectedHook?.settings?.captain_modules?.customers !== false ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-slate-100 text-slate-500'">
                    {{ connectedHook?.settings?.captain_modules?.customers !== false ? 'AKTIF' : 'NONAKTIF' }}
                  </span>
                </div>

                <div class="p-2.5 rounded-lg border border-n-weak bg-n-alpha-1 flex items-center justify-between text-xs">
                  <span class="font-semibold text-n-slate-12 flex items-center gap-2">
                    <span class="i-lucide-share-2 size-4 text-cyan-600"></span> Master Sales Channels
                  </span>
                  <span class="text-[10px] font-bold px-2 py-0.5 rounded-full" :class="connectedHook?.settings?.captain_modules?.channels !== false ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-slate-100 text-slate-500'">
                    {{ connectedHook?.settings?.captain_modules?.channels !== false ? 'AKTIF' : 'NONAKTIF' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- API Traffic Monitor & Real-time Audit Logs -->
            <div class="mt-4 border-t border-n-weak pt-5 flex flex-col gap-4">
              <div class="flex items-center justify-between">
                <div>
                  <h4 class="text-sm font-bold text-n-slate-12 flex items-center gap-2">
                    <span class="i-lucide-activity size-4 text-w-brand-primary"></span>
                    API Traffic Monitor & Real-time Audit Log
                  </h4>
                  <p class="text-xs text-n-slate-11">
                    Pantau riwayat panggilan API dari Captain AI ke Cetakers Flow, durasi respon, dan status keberhasilan.
                  </p>
                </div>
                <button
                  type="button"
                  class="px-3 py-1.5 text-xs font-semibold text-w-brand-primary bg-w-brand-primary/10 rounded-lg hover:bg-w-brand-primary/20 transition-colors flex items-center gap-1.5 shrink-0"
                  @click="fetchLogs"
                >
                  <span class="i-lucide-refresh-cw size-3.5" :class="{ 'animate-spin': isFetchingLogs }"></span>
                  Refresh Log
                </button>
              </div>

              <!-- Metrics Cards -->
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div class="bg-n-card border border-n-weak rounded-xl p-3.5 flex flex-col gap-1 shadow-xs">
                  <span class="text-xs font-medium text-n-slate-11 flex items-center gap-1.5">
                    <span class="i-lucide-bar-chart-2 size-3.5 text-blue-600"></span> Total Requests
                  </span>
                  <span class="text-xl font-bold text-n-slate-12">{{ metrics.total_requests || 0 }}</span>
                </div>
                <div class="bg-n-card border border-n-weak rounded-xl p-3.5 flex flex-col gap-1 shadow-xs">
                  <span class="text-xs font-medium text-n-slate-11 flex items-center gap-1.5">
                    <span class="i-lucide-check-circle2 size-3.5 text-emerald-600"></span> Berhasil (2xx)
                  </span>
                  <span class="text-xl font-bold text-emerald-700 dark:text-emerald-400">{{ metrics.success_count || 0 }}</span>
                </div>
                <div class="bg-n-card border border-n-weak rounded-xl p-3.5 flex flex-col gap-1 shadow-xs">
                  <span class="text-xs font-medium text-n-slate-11 flex items-center gap-1.5">
                    <span class="i-lucide-x-circle size-3.5 text-ruby-600"></span> Gagal (4xx/5xx)
                  </span>
                  <span class="text-xl font-bold text-ruby-700 dark:text-ruby-400">{{ metrics.failed_count || 0 }}</span>
                </div>
                <div class="bg-n-card border border-n-weak rounded-xl p-3.5 flex flex-col gap-1 shadow-xs">
                  <span class="text-xs font-medium text-n-slate-11 flex items-center gap-1.5">
                    <span class="i-lucide-zap size-3.5 text-amber-600"></span> Rata-rata Latency
                  </span>
                  <span class="text-xl font-bold text-n-slate-12">{{ metrics.avg_latency_ms || 0 }} <span class="text-xs font-normal text-n-slate-11">ms</span></span>
                </div>
              </div>

              <!-- Activity Log Table -->
              <div class="border border-n-weak rounded-xl overflow-hidden bg-n-card shadow-xs">
                <div class="overflow-x-auto">
                  <table class="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr class="bg-n-alpha-2 text-n-slate-11 font-semibold border-b border-n-weak">
                        <th class="py-2.5 px-3.5">Waktu</th>
                        <th class="py-2.5 px-3.5">Layanan Modul</th>
                        <th class="py-2.5 px-3.5">Method & Endpoint</th>
                        <th class="py-2.5 px-3.5">Status HTTP</th>
                        <th class="py-2.5 px-3.5">Latency</th>
                        <th class="py-2.5 px-3.5 text-right">Aksi</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-n-weak text-n-slate-12">
                      <tr v-if="!logs.length" class="text-center text-n-slate-11 py-6">
                        <td colspan="6" class="py-8 text-center text-n-slate-11">
                          <span class="i-lucide-inbox size-6 block mx-auto mb-2 text-n-slate-9"></span>
                          Belum ada riwayat aktivitas panggilan API Cetakers Flow. Log akan otomatis tercatat saat Captain AI merespon.
                        </td>
                      </tr>
                      <tr v-for="logItem in logs" :key="logItem.id" class="hover:bg-n-alpha-1 transition-colors">
                        <td class="py-2.5 px-3.5 font-mono text-[11px] text-n-slate-11 whitespace-nowrap">
                          {{ new Date(logItem.created_at).toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'medium' }) }}
                        </td>
                        <td class="py-2.5 px-3.5 font-semibold text-n-slate-12 whitespace-nowrap">
                          {{ logItem.service_name }}
                        </td>
                        <td class="py-2.5 px-3.5 font-mono text-[11px] text-n-slate-11 truncate max-w-xs" :title="logItem.endpoint">
                          <span class="font-bold text-w-brand-primary">{{ logItem.http_method }}</span> {{ logItem.endpoint }}
                        </td>
                        <td class="py-2.5 px-3.5 whitespace-nowrap">
                          <span
                            class="px-2 py-0.5 text-[10px] font-bold rounded-full"
                            :class="logItem.status_code >= 200 && logItem.status_code < 300 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-ruby-100 text-ruby-800 dark:bg-ruby-950 dark:text-ruby-300'"
                          >
                            {{ logItem.status_code }} {{ logItem.status_code >= 200 && logItem.status_code < 300 ? 'OK' : 'ERROR' }}
                          </span>
                        </td>
                        <td class="py-2.5 px-3.5 font-mono text-[11px] whitespace-nowrap">
                          {{ logItem.duration_ms }} ms
                        </td>
                        <td class="py-2.5 px-3.5 text-right whitespace-nowrap">
                          <button
                            type="button"
                            class="px-2.5 py-1 text-[11px] font-semibold text-w-brand-primary bg-w-brand-primary/10 hover:bg-w-brand-primary/20 rounded-md transition-colors"
                            @click="openLogDetail(logItem)"
                          >
                            Detail Log
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Modal Konfigurasi -->
    <woot-modal v-model:show="showConfigModal" :on-close="closeConfigModal">
      <woot-modal-header
        :header-title="isConnected ? 'Edit Konfigurasi Cetakers Flow' : 'Konfigurasi API Cetakers Flow'"
        header-content="Masukkan kredensial API dan atur izin akses Captain AI."
      />
      <div class="p-6">
        <div class="flex flex-col gap-5">
          <div class="flex flex-col gap-1.5">
            <label
              for="cetakers-api-key"
              class="text-sm font-medium text-n-slate-12"
            >
              API Key <span class="text-n-ruby-9">*</span>
            </label>
            <input
              id="cetakers-api-key"
              v-model="apiKey"
              type="text"
              placeholder="Masukkan API Key Cetakers Flow Anda"
              class="w-full rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-w-brand-primary"
            />
          </div>

          <div class="flex flex-col gap-1.5">
            <label
              for="cetakers-endpoint"
              class="text-sm font-medium text-n-slate-12"
            >
              API Endpoint URL (Opsional)
            </label>
            <input
              id="cetakers-endpoint"
              v-model="apiEndpoint"
              type="text"
              placeholder="https://api.cetakers.com/v1"
              class="w-full rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-w-brand-primary"
            />
            <span class="text-xs text-n-slate-11">Kosongkan jika menggunakan endpoint default.</span>
          </div>

          <!-- Section Captain AI Module Permission Toggles -->
          <div class="border-t border-n-weak pt-4 flex flex-col gap-3">
            <div class="bg-n-alpha-1 border border-n-weak rounded-2xl p-4 flex flex-col gap-3">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="size-10 rounded-xl bg-w-brand-primary/10 flex items-center justify-center text-w-brand-primary shrink-0">
                    <span class="i-lucide-sparkles size-5"></span>
                  </div>
                  <div>
                    <h4 class="text-sm font-bold text-n-slate-12 flex items-center gap-1.5">
                      Izinkan Captain AI Mengakses Data Cetakers Flow
                    </h4>
                    <p class="text-xs text-n-slate-11">
                      Captain AI dapat otomatis mengecek SPK, Invoice, Resi, dan Katalog Produk saat membalas pesan.
                    </p>
                  </div>
                </div>
                <input
                  v-model="captainEnabled"
                  type="checkbox"
                  class="size-5 rounded text-w-brand-primary focus:ring-w-brand-primary cursor-pointer shrink-0 ml-2"
                />
              </div>

              <!-- Scope Control & Granular Matrix UI -->
              <div v-if="captainEnabled" class="flex flex-col gap-3 pt-3 border-t border-n-weak mt-1">
                <div class="flex items-center justify-between">
                  <span class="text-xs font-bold text-n-slate-12 uppercase tracking-wider">Izin Modul per Scope (Granular Matrix):</span>
                </div>

                <!-- Scope Tabs Header -->
                <div class="flex border-b border-n-weak gap-1">
                  <button
                    type="button"
                    class="px-3 py-2 text-xs font-semibold rounded-t-lg transition-colors flex items-center gap-1.5 border-b-2"
                    :class="activeScopeTab === 'live_chat' ? 'border-w-brand-primary text-w-brand-primary bg-n-alpha-1' : 'border-transparent text-n-slate-11 hover:text-n-slate-12'"
                    @click="activeScopeTab = 'live_chat'"
                  >
                    <span class="i-lucide-message-square size-3.5 text-blue-600"></span> Live Percakapan
                  </button>
                  <button
                    type="button"
                    class="px-3 py-2 text-xs font-semibold rounded-t-lg transition-colors flex items-center gap-1.5 border-b-2"
                    :class="activeScopeTab === 'copilot' ? 'border-w-brand-primary text-w-brand-primary bg-n-alpha-1' : 'border-transparent text-n-slate-11 hover:text-n-slate-12'"
                    @click="activeScopeTab = 'copilot'"
                  >
                    <span class="i-lucide-bot size-3.5 text-purple-600"></span> Agent Copilot
                  </button>
                  <button
                    type="button"
                    class="px-3 py-2 text-xs font-semibold rounded-t-lg transition-colors flex items-center gap-1.5 border-b-2"
                    :class="activeScopeTab === 'playground' ? 'border-w-brand-primary text-w-brand-primary bg-n-alpha-1' : 'border-transparent text-n-slate-11 hover:text-n-slate-12'"
                    @click="activeScopeTab = 'playground'"
                  >
                    <span class="i-lucide-flask-conical size-3.5 text-amber-600"></span> Playground
                  </button>
                </div>

                <!-- Active Scope Content Panel -->
                <div class="bg-n-card p-4 rounded-xl border border-n-weak flex flex-col gap-3">
                  <!-- Scope Toggle Header & Presets -->
                  <div class="flex items-center justify-between pb-2 border-b border-n-weak">
                    <label class="flex items-center gap-2 cursor-pointer">
                      <input
                        :checked="isScopeEnabled(activeScopeTab)"
                        type="checkbox"
                        class="size-4 rounded text-w-brand-primary cursor-pointer"
                        @change="toggleScopeEnabled(activeScopeTab, $event.target.checked)"
                      />
                      <span class="text-xs font-bold text-n-slate-12">
                        Aktifkan AI di {{ activeScopeTab === 'live_chat' ? 'Live Percakapan' : activeScopeTab === 'copilot' ? 'Agent Copilot' : 'Playground' }}
                      </span>
                    </label>

                    <div v-if="isScopeEnabled(activeScopeTab)" class="flex gap-1.5 items-center">
                      <button
                        type="button"
                        class="text-[11px] font-medium text-w-brand-primary hover:underline cursor-pointer"
                        @click="selectAllModulesForScope(activeScopeTab)"
                      >
                        Pilih Semua
                      </button>
                      <span class="text-n-slate-9 text-xs">|</span>
                      <button
                        type="button"
                        class="text-[11px] font-medium text-amber-600 hover:underline cursor-pointer"
                        @click="selectProductsOnlyForScope(activeScopeTab)"
                      >
                        Hanya Produk
                      </button>
                      <span class="text-n-slate-9 text-xs">|</span>
                      <button
                        type="button"
                        class="text-[11px] font-medium text-n-slate-10 hover:underline cursor-pointer"
                        @click="clearAllModulesForScope(activeScopeTab)"
                      >
                        Kosongkan
                      </button>
                    </div>
                  </div>

                  <!-- Modules Checkboxes Grid -->
                  <div v-if="isScopeEnabled(activeScopeTab)" class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label class="flex items-center justify-between text-xs font-semibold text-n-slate-12 p-2 hover:bg-n-alpha-2 rounded-lg cursor-pointer transition-colors">
                      <span class="flex items-center gap-2">
                        <span class="i-lucide-file-text size-4 text-blue-600"></span> Data SPK & Order
                      </span>
                      <input v-model="captainScopeModules[activeScopeTab].spk" type="checkbox" class="size-4 rounded text-w-brand-primary" />
                    </label>
                    <label class="flex items-center justify-between text-xs font-semibold text-n-slate-12 p-2 hover:bg-n-alpha-2 rounded-lg cursor-pointer transition-colors">
                      <span class="flex items-center gap-2">
                        <span class="i-lucide-shopping-bag size-4 text-indigo-600"></span> Master Produk & Harga
                      </span>
                      <input v-model="captainScopeModules[activeScopeTab].products" type="checkbox" class="size-4 rounded text-w-brand-primary" />
                    </label>
                    <label class="flex items-center justify-between text-xs font-semibold text-n-slate-12 p-2 hover:bg-n-alpha-2 rounded-lg cursor-pointer transition-colors">
                      <span class="flex items-center gap-2">
                        <span class="i-lucide-credit-card size-4 text-emerald-600"></span> Data Invoice & Tagihan
                      </span>
                      <input v-model="captainScopeModules[activeScopeTab].invoices" type="checkbox" class="size-4 rounded text-w-brand-primary" />
                    </label>
                    <label class="flex items-center justify-between text-xs font-semibold text-n-slate-12 p-2 hover:bg-n-alpha-2 rounded-lg cursor-pointer transition-colors">
                      <span class="flex items-center gap-2">
                        <span class="i-lucide-truck size-4 text-amber-600"></span> Pengiriman & Resi
                      </span>
                      <input v-model="captainScopeModules[activeScopeTab].shipping" type="checkbox" class="size-4 rounded text-w-brand-primary" />
                    </label>
                    <label class="flex items-center justify-between text-xs font-semibold text-n-slate-12 p-2 hover:bg-n-alpha-2 rounded-lg cursor-pointer transition-colors">
                      <span class="flex items-center gap-2">
                        <span class="i-lucide-users size-4 text-purple-600"></span> Data Master Pelanggan
                      </span>
                      <input v-model="captainScopeModules[activeScopeTab].customers" type="checkbox" class="size-4 rounded text-w-brand-primary" />
                    </label>
                    <label class="flex items-center justify-between text-xs font-semibold text-n-slate-12 p-2 hover:bg-n-alpha-2 rounded-lg cursor-pointer transition-colors">
                      <span class="flex items-center gap-2">
                        <span class="i-lucide-share-2 size-4 text-cyan-600"></span> Master Sales Channels
                      </span>
                      <input v-model="captainScopeModules[activeScopeTab].channels" type="checkbox" class="size-4 rounded text-w-brand-primary" />
                    </label>
                    <label class="flex items-center justify-between text-xs font-semibold text-n-slate-12 p-2 hover:bg-n-alpha-2 rounded-lg cursor-pointer transition-colors sm:col-span-2">
                      <span class="flex items-center gap-2">
                        <span class="i-lucide-plus-circle size-4 text-rose-600"></span> Buat Pesanan Baru ERP
                      </span>
                      <input v-model="captainScopeModules[activeScopeTab].create_order" type="checkbox" class="size-4 rounded text-w-brand-primary" />
                    </label>
                  </div>

                  <div v-else class="py-4 text-center text-xs text-n-slate-10 italic">
                    Penggunaan AI di {{ activeScopeTab === 'live_chat' ? 'Live Percakapan' : activeScopeTab === 'copilot' ? 'Agent Copilot' : 'Playground' }} dinonaktifkan.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-3 mt-8">
          <Button
            variant="ghost"
            color="slate"
            @click="closeConfigModal"
          >
            Batal
          </Button>
          <Button
            color="blue"
            :is-loading="isSaving"
            :disabled="!apiKey.trim()"
            @click="handleConnect"
          >
            {{ isConnected ? 'Simpan Perubahan' : 'Simpan & Sambungkan' }}
          </Button>
        </div>
      </div>
    </woot-modal>

    <CetakersLogDetailDialog
      :show="showDetailDialog"
      :log="selectedLog"
      @close="showDetailDialog = false"
    />
  </SettingsLayout>
</template>
